import React from 'react';

import { Route, Routes, BrowserRouter, Navigate, useParams } from 'react-router-dom';
import './App.scss';

import { Provider } from 'jotai';
import { store } from './services/atom.service';
import { PrivateRoutes } from './utils/PrivateRoutes';

import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { PageNotFound } from './utils/PageNotFound';
import AppHeader from './components/AppHeader/AppHeader';
import Home from './pages/Home';
import { Login } from './pages/Login';
import { Privacy } from './pages/Privacy';
import { Pricing } from './pages/Pricing';
import { CookieBanner } from './components/CookieBanner/CookieBanner';
import { TelemetryTracker } from './utils/TelemetryTracker';
import { NewHackathon } from './pages/HackathonPage/NewHackathon';
import { HackathonPage } from './pages/HackathonPage/HackathonPage';
import { EditHackathon } from './pages/HackathonPage/EditHackathon';
import { FullIdeaPage } from './pages/IdeaPage/FullIdeaPage';
import { OrganizerPage } from './pages/HackathonPage/OrganizerPage';
import { UpgradePage } from './pages/HackathonPage/UpgradePage';
import { JudgePage } from './pages/HackathonPage/JudgePage';
import { AppFooter } from './components/AppFooter/AppFooter';
import { VotePage } from './pages/HackathonPage/VotePage';
import Box from '@mui/material/Box/Box';
import { HowToRoutes } from './pages/HowTo/HowTo';
import { AdminHomePage } from './pages/Admin/AdminHomePage';
import { OrganizationSettings } from './pages/Organization/OrganizationSettings';
import { NewOrganization } from './pages/Organization/NewOrganization';
import { ManageSessions } from './pages/Sessions/ManageSessions';
import { SessionPage } from './pages/Sessions/SessionPage';
import { AppThemeProvider } from './components/Theme/AppThemeProvider';

// the old Presenters tool is now each session's run-of-show
const PresentersRedirect = () => {
    const { id } = useParams();
    return <Navigate to={`/h/${id}/sessions`} replace />;
}

// addresses people guess from the page names ("Judging", "Organize")
const HackathonAlias = ({ to }: { to: string }) => {
    const { id } = useParams();
    return <Navigate to={`/h/${id}/${to}`} replace />;
}

export default function App() {
    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Provider store={store}>
                <BrowserRouter>
                    <TelemetryTracker />
                    <AppThemeProvider>
                        <Box className="App" sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
                            <AppHeader />
                            <Box component="main" sx={{ flex: 1, py: { xs: 2, md: 4 } }}>
                                <Routes>
                                    <Route path="/" element={<Home />} />
                                    <Route path="/login" element={<Login />} />
                                    <Route path="/privacy" element={<Privacy />} />
                                    <Route path="/pricing" element={<Pricing />} />

                                    <Route path="/h/:id" element={<HackathonPage />} />
                                    {/* signed out, it's set up as a draft in the browser and created after signing in */}
                                    <Route path="/new" element={<NewHackathon />} />
                                    <Route element={<PrivateRoutes />}>
                                        <Route path="/h/:id/organizer" element={<OrganizerPage />} />
                                        <Route path="/h/:id/upgrade" element={<UpgradePage />} />
                                        <Route path="/h/:id/judge" element={<JudgePage />} />
                                        <Route path="/h/:id/vote" element={<VotePage />} />
                                        <Route path="/h/:id/edit" element={<EditHackathon />} />
                                        <Route path="/h/:id/idea/:ideaId" element={<FullIdeaPage />} />
                                        <Route path="/h/:id/presenters" element={<PresentersRedirect />} />
                                        <Route path="/h/:id/judging" element={<HackathonAlias to="judge" />} />
                                        <Route path="/h/:id/organize" element={<HackathonAlias to="organizer" />} />
                                        <Route path="/h/:id/sessions" element={<ManageSessions />} />
                                        <Route path="/h/:id/s/:sessionId" element={<SessionPage />} />
                                        <Route path="/org/new" element={<NewOrganization />} />
                                        <Route path="/org/:id" element={<OrganizationSettings />} />
                                        <Route path="/admin" element={<AdminHomePage />} />
                                    </Route>
                                    {HowToRoutes}
                                    <Route path="*" element={<PageNotFound />} />
                                </Routes>
                            </Box>
                            <AppFooter />
                            <CookieBanner />
                        </Box>
                    </AppThemeProvider>
                </BrowserRouter>
            </Provider>
        </LocalizationProvider>
    )
}
